import { useCallback, useMemo, FormEvent } from "react";
import { Box, IconButton } from "@mui/material";
import {
  Filter,
  FilterConfig,
  filterApiParams,
  PrimaryButton,
  InlineButton,
  colorsLight,
  Tooltip,
  TextField,
  MultiSelectDropdown,
  SingleSelectDropdown,
  DropdownOption,
  SelectOptionsHook,
} from "@coherehealth/common";
import ClearIcon from "@mui/icons-material/Clear";
import { AdvancedSearchServiceCaseRequest } from "@coherehealth/qm-api";
import { ResolvedField } from "./useSearchFilterConfigs";
import { buildSearchRequest } from "./buildSearchRequest";
import { FieldId, FieldSpec, FormValues, Section } from "./fieldRegistry";
import FilterDropdownWithSearch from "./FilterDropdownWithSearch";

export interface SearchPanelProps {
  formValues: FormValues;
  setField: (fieldId: FieldId, value: unknown) => void;
  resetAll: () => void;
  onSearch: (flatParams: Record<string, unknown>, apiRequest: AdvancedSearchServiceCaseRequest) => void;
  resolvedFields: ResolvedField[];
  filterConfigs: FilterConfig[];
  isSearching?: boolean;
}

const SECTION_LABELS: Record<Section, string> = {
  CASE_INFORMATION: "Case information",
  MEMBER_INFORMATION: "Member information",
};

const MAX_VISIBLE_DROPDOWN_ENTRIES = 5;
const DROPDOWN_ENTRY_HEIGHT_PX = 48;
const SINGLE_SELECT_MENU_MAX_HEIGHT = MAX_VISIBLE_DROPDOWN_ENTRIES * DROPDOWN_ENTRY_HEIGHT_PX;

function disabledTooltipFor(spec: FieldSpec, formValues: FormValues): string {
  if (spec.id === "delegatedVendor" && formValues.healthPlan) {
    return "No delegated vendors are available for this health plan.";
  }
  return spec.disabledTooltip ?? "";
}

// Clear-button right offsets vary by field type because each dropdown component
// positions its end icon (chevron or search) differently:
//   LookupSearch (MultipleSelect): search icon at ~32px → clear at 40
//   MultiSelect (MultiSelectDropdown): chevron at ~24px → clear at 32
//   SingleSelect (SingleSelectDropdown): wider chevron at ~28px → clear at 36
function ClearFieldButton({ label, right, onClear }: { label: string; right: number; onClear: () => void }) {
  return (
    <IconButton
      aria-label={`Clear ${label}`}
      disableRipple
      style={{
        position: "absolute",
        right,
        top: "50%",
        transform: "translateY(-50%)",
        padding: 4,
        color: colorsLight.font.secondary,
      }}
      onClick={(e) => {
        // stopPropagation prevents the click from bubbling to MUI Select/Autocomplete
        // and toggling the dropdown open while clearing.
        e.stopPropagation();
        onClear();
      }}
    >
      <ClearIcon />
    </IconButton>
  );
}

// 250px is safe at all grid breakpoints: md (3-col on ~1200px = ~400px/cell),
// sm (2-col) and xs (1-col) are even wider.
const TRUNCATION_STYLE: React.CSSProperties = {
  display: "inline-block",
  maxWidth: 250,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
  verticalAlign: "bottom",
  pointerEvents: "none",
};

export default function SearchPanel({
  formValues,
  setField,
  resetAll,
  onSearch,
  resolvedFields,
  filterConfigs,
  isSearching,
}: SearchPanelProps) {
  const handleSearch = useCallback(
    (e?: FormEvent) => {
      if (e) {
        e.preventDefault();
      }
      const flatParams = filterApiParams(filterConfigs, formValues as Record<string, unknown>);
      const apiRequest = buildSearchRequest(flatParams as Record<string, unknown>);
      onSearch(flatParams as Record<string, unknown>, apiRequest);
    },
    [filterConfigs, formValues, onSearch]
  );

  const handleFieldChange = useCallback(
    (key: string, newValue: unknown) => {
      setField(key as FieldId, newValue);
    },
    [setField]
  );

  const hasAnyFilter = useMemo(
    () => Object.keys(filterApiParams(filterConfigs, formValues as Record<string, unknown>)).length > 0,
    [filterConfigs, formValues]
  );

  const groupedBySections = useMemo(
    () =>
      resolvedFields.reduce((acc, rf) => {
        const section = rf.spec.section;
        if (!acc[section]) {
          acc[section] = [];
        }
        acc[section].push(rf);
        return acc;
      }, {} as Record<Section, typeof resolvedFields>),
    [resolvedFields]
  );

  return (
    <form onSubmit={handleSearch} data-testid="search-panel">
      <Box sx={{ display: "flex", flexDirection: "column", gap: "32px" }}>
        {(["CASE_INFORMATION", "MEMBER_INFORMATION"] as Section[]).map((section) => {
          const fields = groupedBySections[section];
          if (!fields || fields.length === 0) {
            return null;
          }
          return (
            <Box key={section} sx={{ display: "flex", flexDirection: "column", gap: "16px" }}>
              <p
                style={{
                  fontFamily: "Figtree, sans-serif",
                  fontWeight: 500,
                  fontSize: 12,
                  lineHeight: 1.2,
                  letterSpacing: 0,
                  color: colorsLight.font.light,
                  textTransform: "uppercase",
                  margin: 0,
                }}
              >
                {SECTION_LABELS[section]}
              </p>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: {
                    xs: "1fr",
                    sm: "repeat(2, 1fr)",
                    md: "repeat(3, 1fr)",
                  },
                  gap: "16px",
                }}
              >
                {fields.map((rf) => {
                  let filterElement;
                  if (rf.spec.filterType === "TextField") {
                    const textValue = Array.isArray(formValues[rf.spec.id])
                      ? (formValues[rf.spec.id] as string[])[0] ?? ""
                      : (formValues[rf.spec.id] as string) ?? "";
                    filterElement = (
                      <TextField
                        label={rf.spec.label}
                        value={textValue}
                        onChangeValue={(val: string) => {
                          handleFieldChange(rf.spec.id, val ? [val] : []);
                        }}
                        clearable={Boolean(textValue)}
                        fullWidth
                        autoComplete="off"
                      />
                    );
                  } else if (rf.spec.filterType === "SearchableMultiSelect") {
                    const searchConfig = rf.filterConfig as {
                      options: DropdownOption[];
                      label: string;
                      key: string;
                      useOptions?: SelectOptionsHook<DropdownOption>;
                    };
                    const searchSelected = (formValues[rf.spec.id] as DropdownOption[]) || [];
                    const searchSelectedValues = searchSelected.map((v) => v.id);
                    filterElement = (
                      <div style={{ position: "relative" }}>
                        <FilterDropdownWithSearch
                          label={searchConfig.label}
                          options={searchConfig.options}
                          useOptions={searchConfig.useOptions}
                          selectedValues={searchSelectedValues}
                          onChange={(selectedIds: string[]) => {
                            if (searchConfig.useOptions) {
                              // Lazy mode: id === label (user name), so reconstruct any newly
                              // selected option that isn't already in formValues from its id.
                              const selectedOptions = selectedIds.map((id) => {
                                const existing = searchSelected.find((o) => o.id === id);
                                return existing ?? { id, label: id };
                              });
                              handleFieldChange(rf.spec.id, selectedOptions);
                            } else {
                              const selectedOptions = searchConfig.options.filter(({ id }) => selectedIds.includes(id));
                              handleFieldChange(rf.spec.id, selectedOptions);
                            }
                          }}
                        />
                        {searchSelected.length > 0 && !rf.disabled && (
                          <ClearFieldButton
                            label={searchConfig.label}
                            right={36}
                            onClear={() => handleFieldChange(rf.spec.id, [])}
                          />
                        )}
                      </div>
                    );
                  } else if (rf.spec.filterType === "MultiSelect") {
                    const multiConfig = rf.filterConfig as { options: DropdownOption[]; label: string; key: string };
                    const selected = (formValues[rf.spec.id] as DropdownOption[]) || [];
                    const selectedValues = selected.map((v) => v.id);
                    const selectedLabels = selected.map((v) => v.label || v.id).join(", ");
                    filterElement = (
                      <div style={{ position: "relative" }}>
                        <MultiSelectDropdown
                          label={multiConfig.label}
                          options={multiConfig.options}
                          selectedValues={selectedValues}
                          onChange={(selectedIds: string[]) => {
                            const selectedOptions = multiConfig.options.filter(({ id }) => selectedIds.includes(id));
                            handleFieldChange(rf.spec.id, selectedOptions);
                          }}
                          maxMenuHeight={rf.spec.maxMenuHeight}
                          // renderSingleSelection: renders a single truncated text string instead of
                          // individual chips, regardless of how many options are selected
                          renderSingleSelection={
                            selected.length > 0 ? (
                              <span style={TRUNCATION_STYLE} title={selectedLabels}>
                                {selectedLabels}
                              </span>
                            ) : undefined
                          }
                        />
                        {selected.length > 0 && !rf.disabled && (
                          <ClearFieldButton
                            label={multiConfig.label}
                            right={32}
                            onClear={() => handleFieldChange(rf.spec.id, [])}
                          />
                        )}
                      </div>
                    );
                  } else if (rf.spec.filterType === "SingleSelect") {
                    const singleConfig = rf.filterConfig as { options: DropdownOption[]; label: string; key: string };
                    const selectedOption = formValues[rf.spec.id] as DropdownOption | null | undefined;
                    filterElement = (
                      <div style={{ position: "relative" }}>
                        <SingleSelectDropdown
                          label={singleConfig.label}
                          options={singleConfig.options}
                          value={selectedOption?.id || ""}
                          maxMenuHeight={SINGLE_SELECT_MENU_MAX_HEIGHT}
                          disabled={rf.disabled}
                          onChange={(selectedId: string) => {
                            const option = singleConfig.options.find(({ id }) => id === selectedId);
                            handleFieldChange(rf.spec.id, option || null);
                          }}
                        />
                        {selectedOption && !rf.disabled && (
                          <ClearFieldButton
                            label={singleConfig.label}
                            right={36}
                            onClear={() => handleFieldChange(rf.spec.id, null)}
                          />
                        )}
                      </div>
                    );
                  } else if (rf.spec.filterType === "DatePicker") {
                    filterElement = (
                      <Filter
                        filterConfig={rf.filterConfig}
                        value={formValues[rf.spec.id]}
                        onChange={handleFieldChange}
                      />
                    );
                  } else {
                    if (process.env.NODE_ENV !== "production") {
                      // eslint-disable-next-line no-console
                      console.error(`SearchPanel: unhandled filterType "${rf.spec.filterType}"`);
                    }
                    filterElement = (
                      <Filter
                        filterConfig={rf.filterConfig}
                        value={formValues[rf.spec.id]}
                        onChange={handleFieldChange}
                      />
                    );
                  }

                  return rf.disabled ? (
                    <Tooltip key={rf.spec.id} title={disabledTooltipFor(rf.spec, formValues)}>
                      <span style={{ display: "block" }}>
                        <div style={{ opacity: 0.5, pointerEvents: "none" }} aria-disabled="true">
                          {filterElement}
                        </div>
                      </span>
                    </Tooltip>
                  ) : (
                    <div key={rf.spec.id}>{filterElement}</div>
                  );
                })}
              </Box>
            </Box>
          );
        })}

        <Box sx={{ display: "flex", gap: "16px", alignItems: "center", marginTop: "8px" }}>
          <PrimaryButton
            type="submit"
            disabled={isSearching || !hasAnyFilter}
            loading={isSearching}
            data-testid="search-button"
          >
            Search
          </PrimaryButton>
          <InlineButton onClick={resetAll} data-testid="clear-all-button">
            Clear all
          </InlineButton>
        </Box>
      </Box>
    </form>
  );
}
